import type { RequiredGeneric } from '@pba/utils';
import { isBefore } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { AutoComplete, type DatePickerDateType, Form } from 'ui';
import { getBrowserTimezoneWithOffset } from 'common/utils';
import { useGetParsedStartTime, useGetStartDateTime } from '../hooks';
import { useGetStartTimeOptions } from './hooks';

export type ExtendedStartDateFormValues = {
  startDate: DatePickerDateType;
  startTime: string;
};

const StartDateFormContent = <T extends ExtendedStartDateFormValues = never>(_: RequiredGeneric<T>) => {
  const { t } = useTranslation();
  const startDate = Form.useWatch<ExtendedStartDateFormValues['startDate'] | undefined>('startDate');
  const getStartTimeOptions = useGetStartTimeOptions();
  const startTimeOptions = startDate ? getStartTimeOptions({ startDate }) : [];
  const { offset, timeZone } = getBrowserTimezoneWithOffset();
  const getParsedStartTime = useGetParsedStartTime();
  const getStartDateTime = useGetStartDateTime();

  return (
    <Form.Item<ExtendedStartDateFormValues>
      dependencies={['startDate']}
      label="Starts"
      name="startTime"
      rules={[
        { required: true },
        {
          // eslint-disable-next-line @typescript-eslint/require-await -- we need return promise with async function
          async validator(__: unknown, value?: string) {
            const startTime = getParsedStartTime(value);

            if (value && !startTime) {
              throw new Error(t('Error: Starts is not a valid time'));
            }

            if (startDate && startTime && isBefore(getStartDateTime({ startDate, startTime }), new Date())) {
              throw new Error(t('Error: Starts must be in the future'));
            }
          },
        },
      ]}
      tooltip={t("Times are shown in your browser's timezone ({{timeZone}}, {{offset}})", { offset, timeZone })}
    >
      <AutoComplete options={startTimeOptions} placeholder="Time" />
    </Form.Item>
  );
};

export default StartDateFormContent;
